import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './scheduling-components.css';

interface Job {
  jobId: string;
  jobCode: string;
  jobTitle: string;
  department: string;
  payRateMin: number;
  payRateMax: number;
  defaultPayRate: number;
  payType: string;
  description: string;
  requirements: string[] | string;
  isActive: boolean;
}

interface JobManagementProps {
  storeId: string;
  enterpriseId: string;
}

export const JobManagement: React.FC<JobManagementProps> = ({ 
  storeId, 
  enterpriseId 
}) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddJob, setShowAddJob] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);

  const [newJob, setNewJob] = useState({
    jobCode: '',
    jobTitle: '',
    department: '',
    payRateMin: 0,
    payRateMax: 0,
    defaultPayRate: 0,
    payType: 'hourly' as 'hourly' | 'salary',
    description: '',
    requirements: [] as string[]
  });

  useEffect(() => {
    fetchJobs();
  }, [storeId, enterpriseId]);



  const fetchJobs = async () => {
    try {
      setLoading(true);
      
      // Don't make API call if enterpriseId is empty
      if (!enterpriseId) {
        setJobs([]);
        setLoading(false);
        return;
      }
      
      
      const response = await authenticatedFetch(
        API_ENDPOINTS.jobs(enterpriseId),
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        // Ensure requirements is always an array and handle string format from API
        const jobs = (data.data || data.jobs || []).map((job: any) => ({
          ...job,
          requirements: Array.isArray(job.requirements) 
            ? job.requirements 
            : (typeof job.requirements === 'string' && job.requirements.trim()) 
              ? job.requirements.split(',').map((req: string) => req.trim())
              : []
        }));
        setJobs(jobs);
      } else {
        console.error('Failed to fetch jobs:', response.status, response.statusText);
        setJobs([]); // Set empty array instead of mock data
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]); // Set empty array instead of mock data
    } finally {
      setLoading(false);
    }
  };

  const handleAddJob = async () => {
    try {
      // Validate required fields
      if (!newJob.jobTitle || !newJob.department) {
        alert('Please fill in all required fields (Job Title and Department)');
        return;
      }

      if (!enterpriseId) {
        alert('Please select an enterprise before creating a job');
        return;
      }

      const isEditing = editingJob !== null;
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing 
        ? `${API_ENDPOINTS.jobs(enterpriseId)}/${editingJob.jobId}`
        : API_ENDPOINTS.jobs(enterpriseId);

      const requestData = {
        ...newJob,
        enterpriseId,
        storeId: storeId || undefined,
        // Convert requirements array to string for backend compatibility
        requirements: newJob.requirements.join(', ')
      };
      

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        await response.json();
        alert(`Job ${isEditing ? 'updated' : 'created'} successfully!`);
        
        // Reset form
        setNewJob({
          jobCode: '',
          jobTitle: '',
          department: '',
          payRateMin: 0,
          payRateMax: 0,
          defaultPayRate: 0,
          payType: 'hourly',
          description: '',
          requirements: []
        });
        setEditingJob(null);
        setShowAddJob(false);
        
        // Refresh jobs list
        await fetchJobs();
      } else {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        console.error(`Job ${isEditing ? 'update' : 'creation'} failed:`, errorMessage);
        alert(`Failed to ${isEditing ? 'update' : 'create'} job: ${errorMessage}`);
      }
    } catch (error) {
      console.error(`Error ${editingJob ? 'updating' : 'adding'} job:`, error);
      alert(`Failed to ${editingJob ? 'update' : 'create'} job. Please try again.`);
    }
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    setNewJob({
      jobCode: job.jobCode || '',
      jobTitle: job.jobTitle || '',
      department: job.department || '',
      payRateMin: job.payRateMin || 0,
      payRateMax: job.payRateMax || 0,
      defaultPayRate: job.defaultPayRate || 0,
      payType: (job.payType as 'hourly' | 'salary') || 'hourly',
      description: job.description || '',
      requirements: Array.isArray(job.requirements) 
        ? job.requirements 
        : (typeof job.requirements === 'string' && job.requirements && job.requirements.trim())
          ? (job.requirements as string).split(',').map((req: string) => req.trim())
          : []
    });
    setShowAddJob(true);
  };

  const handleDeleteJob = async (jobId: string, jobTitle: string) => {
    if (window.confirm(`Are you sure you want to delete the job "${jobTitle}"? This action cannot be undone.`)) {
      try {
        const deleteUrl = `${API_ENDPOINTS.jobs(enterpriseId)}/${jobId}?enterpriseId=${enterpriseId}`;
        
        const response = await authenticatedFetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          await response.json();
          alert('Job deleted successfully!');
          await fetchJobs(); // Refresh the jobs list
        } else {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (e) {
            console.error('Could not parse error response:', e);
          }
          alert(`Failed to delete job: ${errorMessage}`);
        }
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Failed to delete job. Please try again.');
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading jobs...</div>;
  }

  return (
    <>
      <div className="job-management">
      <div className="page-header">
        <h2>Job Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setShowAddJob(true);
          }}
        >
          Add New Job
        </button>
      </div>

      <div className="jobs-grid">
        {jobs.map((job) => (
          <div key={job.jobId} className="job-card">
            <div className="job-header">
              <h3>{job.jobTitle}</h3>
              <span className="job-code">{job.jobCode}</span>
            </div>
            <div className="job-details">
              <p><strong>Department:</strong> {job.department}</p>
              <p><strong>Pay Range:</strong> ${job.payRateMin} - ${job.payRateMax} / {job.payType}</p>
              <p><strong>Default Rate:</strong> ${job.defaultPayRate} / {job.payType}</p>
              {job.description && <p><strong>Description:</strong> {job.description}</p>}
              {(Array.isArray(job.requirements) && job.requirements.length > 0) || 
               (typeof job.requirements === 'string' && job.requirements && job.requirements.trim()) ? (
                <p><strong>Requirements:</strong> {
                  Array.isArray(job.requirements) 
                    ? job.requirements.join(', ')
                    : job.requirements
                }</p>
              ) : null}
            </div>
            <div className="job-status">
              <span className={`status ${job.isActive ? 'active' : 'inactive'}`}>
                {job.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="job-actions">
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => handleEditJob(job)}
                title="Edit job details"
              >
                Edit
              </button>
              <button 
                className="btn btn-sm btn-danger"
                onClick={() => handleDeleteJob(job.jobId, job.jobTitle)}
                title="Delete this job"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {jobs.length === 0 && (
        <div className="empty-state">
          <h3>No Jobs Defined</h3>
          <p>Create job positions to start scheduling employees.</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddJob(true)}
          >
            Create First Job
          </button>
        </div>
      )}

      </div>

      {showAddJob && ReactDOM.createPortal(
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const,
            position: 'relative',
            zIndex: 100000
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>{editingJob ? 'Edit Job' : 'Add New Job'}</h3>
              <button 
                onClick={() => {
                  setShowAddJob(false);
                  setEditingJob(null);
                  setNewJob({
                    jobCode: '',
                    jobTitle: '',
                    department: '',
                    payRateMin: 0,
                    payRateMax: 0,
                    defaultPayRate: 0,
                    payType: 'hourly',
                    description: '',
                    requirements: []
                  });
                }}
                style={{ 
                  fontSize: '24px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px'
                }}
              >
                ×
              </button>
            </div>
            <div style={{
              padding: '20px',
              overflowY: 'auto',
              flex: 1
            }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Job Code *</label>
                  <input
                    type="text"
                    value={newJob.jobCode}
                    onChange={(e) => setNewJob({...newJob, jobCode: e.target.value.toUpperCase()})}
                    placeholder="CASHIER, COOK, MANAGER"
                  />
                </div>
                <div className="form-group">
                  <label>Job Title *</label>
                  <input
                    type="text"
                    value={newJob.jobTitle}
                    onChange={(e) => setNewJob({...newJob, jobTitle: e.target.value})}
                    placeholder="Cashier, Line Cook, Shift Manager"
                  />
                </div>
                <div className="form-group">
                  <label>Department *</label>
                  <select
                    value={newJob.department}
                    onChange={(e) => setNewJob({...newJob, department: e.target.value})}
                  >
                    <option value="">Select Department</option>
                    <option value="Front of House">Front of House</option>
                    <option value="Kitchen">Kitchen</option>
                    <option value="Management">Management</option>
                    <option value="Back of House">Back of House</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Pay Type</label>
                  <select
                    value={newJob.payType}
                    onChange={(e) => setNewJob({...newJob, payType: e.target.value as 'hourly' | 'salary'})}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Minimum Pay Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newJob.payRateMin}
                    onChange={(e) => setNewJob({...newJob, payRateMin: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="form-group">
                  <label>Maximum Pay Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newJob.payRateMax}
                    onChange={(e) => setNewJob({...newJob, payRateMax: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="form-group">
                  <label>Default Pay Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newJob.defaultPayRate}
                    onChange={(e) => setNewJob({...newJob, defaultPayRate: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={newJob.description}
                    onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                    rows={3}
                    placeholder="Job description and responsibilities"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Requirements</label>
                  <input
                    type="text"
                    value={newJob.requirements.join(', ')}
                    onChange={(e) => setNewJob({...newJob, requirements: e.target.value.split(',').map(r => r.trim()).filter(r => r)})}
                    placeholder="Enter requirements separated by commas (e.g., Food Safety Certification, 2+ Years Experience)"
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions" style={{
              padding: '20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowAddJob(false);
                  setEditingJob(null);
                  setNewJob({
                    jobCode: '',
                    jobTitle: '',
                    department: '',
                    payRateMin: 0,
                    payRateMax: 0,
                    defaultPayRate: 0,
                    payType: 'hourly',
                    description: '',
                    requirements: []
                  });
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddJob}>
                {editingJob ? 'Update Job' : 'Create Job'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </>
  );
};